import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, Grid,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
  Snackbar, Alert, Chip, Divider, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Tab, Tabs, InputAdornment, Tooltip,
  IconButton,
} from '@mui/material';
import {
  Search, DirectionsCar, AssignmentReturn, AssignmentTurnedIn,
  ElectricScooter, Refresh, Timeline,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const fmtTime = (t) => {
  if (!t) return '';
  try {
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    return `${(hour % 12) || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  } catch { return t; }
};

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color }) => (
  <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 26 } })}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={800} color="#1a237e" sx={{ lineHeight: 1.1 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
      </Box>
    </Box>
  </Card>
);

export default function Reports() {
  const [vehicles, setVehicles]       = useState([]);
  const [selectedId, setSelectedId]   = useState('');
  const [manualNum, setManualNum]     = useState('');
  const [history, setHistory]         = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [viewMode, setViewMode]       = useState(0); // 0=Timeline, 1=Table
  const [page, setPage]               = useState(0);
  const [pageSize, setPageSize]       = useState(10);
  const [histFilter, setHistFilter]   = useState(''); // '' | 'assigned' | 'returned'
  const [snack, setSnack]             = useState({ open: false, msg: '', sev: 'info' });

  // ── Load vehicles list ──────────────────────────────────────────────────────
  const loadVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    const res = await InvokeApi(Config.base_url + apiList.getVehicles, { page: 1, per_page: 500 });
    if (res?.code === '200') setVehicles(res.vehicles ?? []);
    setLoadingVehicles(false);
  }, []);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  // ── Search — sequential to avoid PHP single-thread queuing issues ───────────
  const handleSearch = async () => {
    const vehicleId  = selectedId || null;
    const vehicleNum = manualNum.trim() || null;
    if (!vehicleId && !vehicleNum) {
      setSnack({ open: true, msg: 'Please select or enter a vehicle.', sev: 'warning' }); return;
    }
    setLoading(true);
    setHistory([]); setVehicleInfo(null);

    // Step 1 — get history
    const res = await InvokeApi(Config.base_url + apiList.getVehicleHistory, {
      id: vehicleId ?? undefined,
      vehicle_number: vehicleNum ?? undefined,
    });

    if (res?.code === '200') {
      setHistory(res.history ?? []);
      if ((res.history ?? []).length === 0) {
        setSnack({ open: true, msg: 'No history found for this vehicle.', sev: 'info' });
      }
    } else {
      // Non-200 on history — show message, don't logout
      setSnack({ open: true, msg: res?.message || 'No data found.', sev: 'info' });
      setLoading(false);
      return;
    }

    // Step 2 — get vehicle detail only if we have an ID (separate sequential call)
    if (vehicleId) {
      // Small delay prevents PHP single-thread collision
      await new Promise(r => setTimeout(r, 150));
      const vRes = await InvokeApi(Config.base_url + apiList.getVehicle, { id: vehicleId });
      if (vRes?.code === '200') setVehicleInfo(vRes?.vehicle ?? null);
    }

    setLoading(false);
  };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const assigned  = history.filter(h => h.event_type === 'assigned').length;
  const returned  = history.filter(h => h.event_type === 'returned').length;
  const filtered  = histFilter ? history.filter(h => h.event_type === histFilter) : history;
  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Vehicle Track Report</Typography>
          <Typography variant="body2" color="text.secondary">Search assignment history for any vehicle</Typography>
        </Box>
        <Tooltip title="Reload vehicle list">
          <IconButton onClick={loadVehicles} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Search card ─────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="#1a237e" sx={{ mb: 2, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
            Select Vehicle
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small" disabled={loadingVehicles}>
                <InputLabel>Choose from list</InputLabel>
                <Select
                  value={selectedId}
                  label="Choose from list"
                  onChange={e => { setSelectedId(e.target.value); setManualNum(''); }}
                >
                  <MenuItem value=""><em>— Select vehicle —</em></MenuItem>
                  {vehicles.map(v => (
                    <MenuItem key={v.id} value={v.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ElectricScooter sx={{ fontSize: 16, color: '#1a237e' }} />
                        <span><strong>{v.vehicle_number}</strong> — {v.model}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center', pt: '12px !important' }}>
              <Box sx={{ px: 1, py: 0.5, borderRadius: 99, bgcolor: '#f0f0f0' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">OR</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small"
                label="Enter vehicle number manually"
                placeholder="e.g. MH01AB1234"
                value={manualNum}
                onChange={e => { setManualNum(e.target.value); setSelectedId(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? null : <Search />}
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700, px: 4, py: 1 }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Vehicle info card ────────────────────────────────────────────────── */}
      {vehicleInfo && (
        <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 44, height: 44, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}>
                <ElectricScooter />
              </Avatar>
              <Box>
                <Typography fontWeight={800} fontSize={16} color="#1a237e">
                  {vehicleInfo.vehicle_number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vehicleInfo.model}{vehicleInfo.color ? ` · ${vehicleInfo.color}` : ''}{vehicleInfo.year ? ` · ${vehicleInfo.year}` : ''}
                </Typography>
              </Box>
              <Chip
                label={vehicleInfo.status || 'active'}
                color={vehicleInfo.status === 'assigned' ? 'primary' : vehicleInfo.status === 'maintenance' ? 'warning' : 'success'}
                size="small" sx={{ ml: 'auto', fontWeight: 700 }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {[
                ['Chassis No.',       vehicleInfo.chassis_number],
                ['Battery',           vehicleInfo.battery_pct ? `${vehicleInfo.battery_pct}%` : null],
                ['Range',             vehicleInfo.range_km ? `${vehicleInfo.range_km} km` : null],
                ['Insurance Expiry',  vehicleInfo.insurance_expiry ? fmtDate(vehicleInfo.insurance_expiry) : null],
              ].filter(([, v]) => v).map(([label, value]) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Results ──────────────────────────────────────────────────────────── */}
      {history.length > 0 && (
        <>
          {/* Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <StatCard icon={<Timeline />}            label="Total Events"  value={history.length} color="#1a237e" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard icon={<AssignmentTurnedIn />}  label="Assignments"   value={assigned}       color="#16a34a" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard icon={<AssignmentReturn />}    label="Returns"       value={returned}       color="#d97706" />
            </Grid>
          </Grid>

          {/* View controls */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0' }}>
              <Tabs
                value={viewMode}
                onChange={(_, v) => setViewMode(v)}
                sx={{ '& .MuiTab-root': { fontWeight: 700, fontSize: 13, minHeight: 40 } }}
              >
                <Tab icon={<Timeline fontSize="small" />}     iconPosition="start" label="Timeline" />
                <Tab icon={<DirectionsCar fontSize="small" />} iconPosition="start" label="Table" />
              </Tabs>
              <FormControl size="small" sx={{ minWidth: 160, ml: 'auto' }}>
                <InputLabel>Filter Events</InputLabel>
                <Select
                  value={histFilter}
                  label="Filter Events"
                  onChange={e => { setHistFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All Events</MenuItem>
                  <MenuItem value="assigned">Assignments Only</MenuItem>
                  <MenuItem value="returned">Returns Only</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* ── Timeline view ─────────────────────────────────────────────── */}
            {viewMode === 0 && (
              <Box sx={{ p: 3 }}>
                {filtered.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                    <Timeline sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography color="text.secondary">No events match the filter.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative', pl: 3 }}>
                    {/* Vertical line */}
                    <Box sx={{ position: 'absolute', left: 11, top: 0, bottom: 0, width: 2, bgcolor: '#e2e8f0' }} />
                    {filtered.map((h, i) => {
                      const isAssigned = h.event_type === 'assigned';
                      const dotColor   = isAssigned ? '#16a34a' : '#d97706';
                      const bgColor    = isAssigned ? '#f0fdf4' : '#fffbeb';
                      const border     = isAssigned ? '#bbf7d0' : '#fde68a';
                      return (
                        <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2.5, position: 'relative' }}>
                          {/* Dot */}
                          <Box sx={{
                            position: 'absolute', left: -17, top: 16,
                            width: 14, height: 14, borderRadius: '50%',
                            bgcolor: dotColor, border: '2px solid #fff',
                            boxShadow: `0 0 0 2px ${dotColor}40`, zIndex: 1,
                          }} />
                          {/* Card */}
                          <Card elevation={0} sx={{ flex: 1, border: `1px solid ${border}`, borderRadius: 2, p: 2, bgcolor: bgColor }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isAssigned
                                  ? <AssignmentTurnedIn sx={{ fontSize: 18, color: '#16a34a' }} />
                                  : <AssignmentReturn sx={{ fontSize: 18, color: '#d97706' }} />}
                                <Typography variant="body2" fontWeight={800}
                                  sx={{ color: isAssigned ? '#15803d' : '#b45309' }}>
                                  {isAssigned ? 'Vehicle Assigned' : 'Vehicle Returned'}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {fmtDate(h.date)}{h.time ? ` · ${fmtTime(h.time)}` : ''}
                              </Typography>
                            </Box>
                            <Grid container spacing={1.5}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Rider</Typography>
                                <Typography variant="body2" fontWeight={600}>{h.user_name || '—'}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                  {h.user_phone || '—'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">
                                  {isAssigned ? 'Assigned By' : 'Collected By'}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>{h.by_name || '—'}</Typography>
                              </Grid>
                              {!isAssigned && h.notes && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">Return Notes</Typography>
                                  <Typography variant="body2">{h.notes}</Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Card>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* ── Table view ────────────────────────────────────────────────── */}
            {viewMode === 1 && (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: '#f5f6fa' }}>
                        {['#', 'Event', 'Rider', 'Phone', 'By', 'Date', 'Time', 'Notes'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                            No events found
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginated.map((h, i) => {
                          const isAssigned = h.event_type === 'assigned';
                          return (
                            <TableRow key={i} hover>
                              <TableCell>
                                <Chip label={`#${i + 1 + page * pageSize}`} size="small"
                                  sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 12 }} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={isAssigned ? 'Assigned' : 'Returned'}
                                  color={isAssigned ? 'success' : 'warning'}
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ width: 30, height: 30, fontSize: 12, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}>
                                    {(h.user_name || 'U')[0].toUpperCase()}
                                  </Avatar>
                                  <Typography fontSize={13} fontWeight={600}>{h.user_name || '—'}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{h.user_phone || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 13 }}>{h.by_name || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{fmtDate(h.date)}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{h.time ? fmtTime(h.time) : '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12, maxWidth: 160 }}>
                                <Typography variant="body2" noWrap>{h.notes || '—'}</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[10, 25, 50]}
                  sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
                />
              </>
            )}
          </Card>
        </>
      )}

      {/* Empty state — after search, zero results */}
      {!loading && history.length === 0 && (vehicleInfo || manualNum) && (
        <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
          <DirectionsCar sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
          <Typography variant="h6" fontWeight={600} color="#64748b">No History Found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This vehicle has no assignment or return events recorded yet.
          </Typography>
        </Box>
      )}

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
